import { useCallback, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  ParameterTimeSeriesConfiguration,
  ParametersLocation,
  ObservationsMapParameterDisplayGroup,
} from "@/lib/api";
import type { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { useRefreshAction } from "@/features/dashboard/features/refresh-timer/hooks";

import {
  buildStationMarkerLayerSeriesRequests,
  configuredTimeSeriesParameterIds,
  keys,
  useObservedLocationParameterSeriesQueries,
} from "./hooks/queries";
import {
  getLocationMarkerLoadState,
  getMarkerDisplayState,
  locationSupportsAnyConfiguredParameter,
  markerHasData,
  type MarkerDisplayState,
} from "./marker-display-state";
import { rankMarkersByOrder } from "./marker-order";
import { useStationMarkersInteraction } from "./station-markers-interaction-context";
import { useMarkerCollision } from "./use-marker-collision";
import {
  areMarkerSeriesQueriesSettled,
  collectMarkerSeriesDatapointTimestamps,
} from "./collect-marker-series-datapoint-timestamps";
import { StationMarker } from "./station-marker";

type StationMarkersLayerViewProps = {
  locations: ParametersLocation[];
  currentTime?: TimeRangeDimensionValue;
  activeDisplayGroup: ObservationsMapParameterDisplayGroup | undefined;
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  resolveParameterLabel: (parameterId: string) => string | undefined;
  onDerivedTimestamps?: (timestamps: string[]) => void;
};

export function StationMarkersLayerView({
  locations,
  currentTime,
  activeDisplayGroup,
  timeSeriesConfigurations,
  resolveParameterLabel,
  onDerivedTimestamps,
}: StationMarkersLayerViewProps) {
  const markerParameterIds = useMemo(
    () => activeDisplayGroup?.parameter_ids ?? [],
    [activeDisplayGroup],
  );
  const chartParameterIds = useMemo(
    () => configuredTimeSeriesParameterIds(timeSeriesConfigurations),
    [timeSeriesConfigurations],
  );
  const { selectedLocationId, onSelectLocation, onAggregateStatusChange } =
    useStationMarkersInteraction();

  const seriesRequests = useMemo(
    () =>
      buildStationMarkerLayerSeriesRequests(
        locations,
        markerParameterIds,
        timeSeriesConfigurations,
      ),
    [locations, markerParameterIds, timeSeriesConfigurations],
  );

  const seriesQueries = useObservedLocationParameterSeriesQueries(seriesRequests);

  // Resolve every marker's display state once; the render map and the ordering
  // comparator both read from this map instead of recomputing per marker.
  const displayStateById = useMemo(() => {
    const byId = new Map<string, MarkerDisplayState>();
    for (const location of locations) {
      byId.set(
        location.id,
        getMarkerDisplayState(
          location,
          markerParameterIds,
          seriesRequests,
          seriesQueries,
          currentTime,
          timeSeriesConfigurations,
          resolveParameterLabel,
        ),
      );
    }
    return byId;
  }, [
    locations,
    markerParameterIds,
    seriesRequests,
    seriesQueries,
    currentTime,
    timeSeriesConfigurations,
    resolveParameterLabel,
  ]);

  // Multi-step ordering: selected first, then markers with data, then the
  // unified dashboard/fallback priority (`locations` is already in that order,
  // so its index is the base priority). Rank drives z-index and declutter.
  const orderRankById = useMemo(() => {
    const inputs = locations.map((location, index) => {
      const displayState = displayStateById.get(location.id);
      return {
        id: location.id,
        selected: location.id === selectedLocationId,
        hasData: displayState ? markerHasData(displayState) : true,
        basePriority: index,
      };
    });
    return rankMarkersByOrder(inputs);
  }, [locations, displayStateById, selectedLocationId]);

  const collisionMarkers = useMemo(
    () =>
      locations.map((location, index) => ({
        id: location.id,
        priority: orderRankById.get(location.id) ?? index,
        pinned: location.id === selectedLocationId,
      })),
    [locations, orderRankById, selectedLocationId],
  );
  const { hiddenIds, registerMarker } = useMarkerCollision(collisionMarkers);

  const anySeriesPending = seriesQueries.some((query) => query.isPending);
  const anySeriesError = seriesQueries.some((query) => query.isError);

  const queryClient = useQueryClient();
  const refreshParameterSeries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: keys.locations() });
  }, [queryClient]);
  useRefreshAction(refreshParameterSeries);

  useEffect(() => {
    onAggregateStatusChange?.({ anyPending: anySeriesPending, anyError: anySeriesError });
  }, [anySeriesPending, anySeriesError, onAggregateStatusChange]);

  useEffect(() => {
    return () => {
      onAggregateStatusChange?.({ anyPending: false, anyError: false });
    };
  }, [onAggregateStatusChange]);

  useEffect(() => {
    if (!onDerivedTimestamps) return;
    if (!areMarkerSeriesQueriesSettled(markerParameterIds, seriesRequests, seriesQueries)) {
      return;
    }

    onDerivedTimestamps(
      collectMarkerSeriesDatapointTimestamps(
        markerParameterIds,
        seriesRequests,
        seriesQueries,
      ),
    );
  }, [markerParameterIds, seriesRequests, seriesQueries, onDerivedTimestamps]);

  return (
    <>
      {locations.map((location, index) => {
        const loadState = getLocationMarkerLoadState(
          location,
          markerParameterIds,
          seriesRequests,
          seriesQueries,
          timeSeriesConfigurations,
        );
        const displayState =
          displayStateById.get(location.id) ??
          getMarkerDisplayState(
            location,
            markerParameterIds,
            seriesRequests,
            seriesQueries,
            currentTime,
            timeSeriesConfigurations,
            resolveParameterLabel,
          );
        const supportsAnyConfiguredChartParameter = locationSupportsAnyConfiguredParameter(
          location,
          chartParameterIds,
        );
        const selected = location.id === selectedLocationId;
        // Non-selected markers with no data at all render as a permanent
        // no-data dot regardless of overlap; selected markers keep their pill.
        const emptyNoData =
          !selected && displayState.phase === "ready" && !markerHasData(displayState);

        return (
          <StationMarker
            key={location.id}
            location={location}
            loadState={loadState}
            displayState={displayState}
            supportsAnyConfiguredChartParameter={supportsAnyConfiguredChartParameter}
            selected={selected}
            onSelect={() => onSelectLocation(location)}
            priorityIndex={orderRankById.get(location.id) ?? index}
            hidden={hiddenIds.has(location.id)}
            emptyNoData={emptyNoData}
            registerRef={registerMarker(location.id)}
          />
        );
      })}
    </>
  );
}
